import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Label } from '@tyndo/ui-kit';
import { AuthShell, AuthError, AuthNotice } from './AuthShell';
import { forgotPassword } from '../auth/nativeAuth';
import { recordError } from '../observability/telemetry';

/**
 * Forgot-password request page. POSTs the email to /api/v1/auth/forgot-password,
 * which is enumeration-safe (always 200), so we always show the same generic
 * "if an account exists, a link is on the way" confirmation.
 */
export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      const result = await forgotPassword(email.trim());
      setNotice(result.message);
    } catch (err) {
      // The endpoint always 200s; only a transport error lands here.
      setError('Could not send the reset email. Please try again.');
      recordError('auth.forgot_password_failed', err, {});
    } finally {
      setLoading(false);
    }
  };

  if (notice) {
    return (
      <AuthShell title="Check your email">
        <div className="space-y-4">
          <AuthNotice>{notice}</AuthNotice>
          <Button onClick={() => navigate('/login')} className="w-full">
            Back to sign in
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Reset your password" subtitle="We'll email you a link">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <AuthError message={error} />}

        <div className="space-y-1.5">
          <Label htmlFor="forgot-email">Email</Label>
          <Input
            id="forgot-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            disabled={loading}
          />
        </div>

        <Button type="submit" size="lg" disabled={loading} className="w-full">
          {loading ? 'Sending…' : 'Send reset link'}
        </Button>
      </form>

      <p className="text-sm text-muted-foreground text-center mt-4">
        <Button
          variant="link"
          type="button"
          onClick={() => navigate('/login')}
          className="h-auto p-0 text-foreground font-medium underline"
        >
          Back to sign in
        </Button>
      </p>
    </AuthShell>
  );
}
